import { Check } from "lucide-react";
import { cn } from "@/components/ui/cn";

/**
 * The author's summary and takeaways — `Module.summary` / `.takeaways` and the
 * same pair on `CommunicationPost` (docs/synced/portal-api/05-types.md). Both
 * are author-written for the active locale: `summary` is null when nobody wrote
 * one, `takeaways` is empty (never null), so the block renders only when there
 * is something in it — an empty "Key points" box is worse than none.
 *
 * Plain text, not HTML: the fields are strings, and rendering them as text is
 * what keeps an author's stray `<` from becoming markup. No hooks, so the
 * client detail views and the server articles share it.
 */
export function KeyPoints({
  title,
  summary,
  takeaways,
  className,
}: {
  title: string;
  summary?: string | null;
  takeaways?: readonly string[] | null;
  className?: string;
}) {
  const text = summary?.trim() || null;
  const points = (takeaways ?? []).map((p) => p.trim()).filter(Boolean);
  if (!text && points.length === 0) return null;
  return (
    <section
      aria-label={title}
      className={cn("key-points rounded-xl border border-border bg-muted/50 px-5 py-4", className)}
    >
      <h2 className="!mb-0 !text-[11px] !font-bold !uppercase !tracking-[0.14em] !text-muted-foreground">
        {title}
      </h2>
      {text ? <p className="mt-2 text-[15px] leading-relaxed text-foreground">{text}</p> : null}
      {points.length > 0 ? (
        <ul className="mt-3 grid list-none gap-2 !pl-0">
          {points.map((point, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 text-[15px] leading-snug text-foreground"
            >
              <Check className="mt-0.5 size-4 shrink-0 text-accent" strokeWidth={2.5} aria-hidden />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
